import React, { useCallback, useEffect, useState } from "react";
import { Download, Eye, Plus, RefreshCw } from "lucide-react";

import api from "../api";

function InvoiceList({ onCreate, onView, onCreateFromQuotation }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ by_status: {}, total_outstanding: 0 });
  const [quotationRows, setQuotationRows] = useState([]);
  const [quotationSummary, setQuotationSummary] = useState({
    accepted_quotations: 0,
    invoiced: 0,
    pending_invoice: 0,
    total_quotation_amount: 0,
    total_due_amount: 0,
  });
  const [filters, setFilters] = useState({ status: "", invoice_type: "" });
  const [loading, setLoading] = useState(true);
  const [creatingQuotationId, setCreatingQuotationId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value)
      ).toString();
      const [listRes, summaryRes, quotationRes] = await Promise.all([
        api.get(`/accounts/invoices${query ? `?${query}` : ""}`),
        api.get("/accounts/invoices/summary"),
        api.get("/accounts/invoices/quotation-sources"),
      ]);
      setRows(listRes.data.data || []);
      setSummary(summaryRes.data || { by_status: {}, total_outstanding: 0 });
      setQuotationRows(quotationRes.data?.data || []);
      setQuotationSummary(quotationRes.data?.summary || {
        accepted_quotations: 0,
        invoiced: 0,
        pending_invoice: 0,
        total_quotation_amount: 0,
        total_due_amount: 0,
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openPdf = (id) => {
    window.open(`http://127.0.0.1:5011/api/v1/accounts/invoices/${id}/pdf`, "_blank", "noopener,noreferrer");
  };

  const handleCreateFromQuotation = async (row) => {
    if (!onCreateFromQuotation) return;
    setCreatingQuotationId(row.quotation_id);
    setError("");
    try {
      onCreateFromQuotation({
        customer_id: row.customer_id || "",
        due_date: "",
        advance_deducted: Number(row.amount_paid || 0),
        discount_pct: Number(row.quotation_discount_pct || 0),
        payment_terms: row.due_amount > 0 ? "Due on receipt" : "Fully settled",
        notes: `Created from accepted quotation ${row.quotation_no}${row.sales_order_code ? ` / order ${row.sales_order_code}` : ""}`,
        sales_order_id: row.sales_order_id || null,
        items: Array.isArray(row.items) && row.items.length
          ? row.items.map((item) => ({
              description: [item.design_name, item.design_code].filter(Boolean).join(" - ") || `Quotation Item ${item.id}`,
              item_type: "GOLD_JEWELLERY",
              quantity: Number(item.quantity || 0) || 1,
              unit: "pcs",
              rate: Number(item.unit_price || 0) || (
                Number(item.total_price || 0) && Number(item.quantity || 0)
                  ? Number(item.total_price || 0) / Number(item.quantity || 1)
                  : 0
              ),
              weight_g: Number(item.total_weight_g || 0) || (
                Number(item.metal_weight_g || 0) && Number(item.quantity || 0)
                  ? Number(item.metal_weight_g || 0) * Number(item.quantity || 0)
                  : ""
              ),
              design_code: item.design_code || "",
            }))
          : [
              {
                description: `Accepted quotation ${row.quotation_no}${row.sales_order_code ? ` (${row.sales_order_code})` : ""}`,
                item_type: "GOLD_JEWELLERY",
                quantity: Number(row.total_pieces || 0) || 1,
                unit: "pcs",
                rate: Number(row.total_pieces || 0)
                  ? Number(row.quotation_amount || 0) / Number(row.total_pieces || 1)
                  : Number(row.quotation_amount || 0),
                weight_g: Number(row.total_weight_g || 0) || "",
                design_code: "",
              },
            ],
      });
    } finally {
      setCreatingQuotationId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="grid gap-4 md:grid-cols-4">
        <SummaryCard label="Draft" value={summary.by_status?.DRAFT?.count || 0} hint={`Rs. ${(summary.by_status?.DRAFT?.total || 0).toFixed(2)}`} />
        <SummaryCard label="Pending Approval" value={summary.by_status?.PENDING_APPROVAL?.count || 0} hint={`Rs. ${(summary.by_status?.PENDING_APPROVAL?.total || 0).toFixed(2)}`} />
        <SummaryCard label="Approved/Sent" value={(summary.by_status?.APPROVED?.count || 0) + (summary.by_status?.SENT?.count || 0)} hint={`Outstanding Rs. ${Number(summary.total_outstanding || 0).toFixed(2)}`} />
        <SummaryCard label="Paid" value={summary.by_status?.PAID?.count || 0} hint={`Rs. ${(summary.by_status?.PAID?.total || 0).toFixed(2)}`} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Accepted Quotations Billing Flow</h3>
            <p className="text-sm text-slate-500">Client-accepted quotations from Sales are shown here first, along with order, production/dispatch progress, invoice amount, paid amount, and due balance.</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-2xl bg-slate-50 px-4 py-2 text-slate-700">Accepted: <b>{quotationSummary.accepted_quotations || 0}</b></div>
            <div className="rounded-2xl bg-emerald-50 px-4 py-2 text-emerald-700">Invoiced: <b>{quotationSummary.invoiced || 0}</b></div>
            <div className="rounded-2xl bg-amber-50 px-4 py-2 text-amber-700">Pending Invoice: <b>{quotationSummary.pending_invoice || 0}</b></div>
            <div className="rounded-2xl bg-indigo-50 px-4 py-2 text-indigo-700">Quotation Value: <b>Rs. {Number(quotationSummary.total_quotation_amount || 0).toFixed(2)}</b></div>
            <div className="rounded-2xl bg-rose-50 px-4 py-2 text-rose-700">Total Due: <b>Rs. {Number(quotationSummary.total_due_amount || 0).toFixed(2)}</b></div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Quotation</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Order / Flow</th>
                <th className="px-4 py-3">Quotation Amount</th>
                <th className="px-4 py-3">Paid</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && quotationRows.map((row) => (
                <tr key={row.quotation_id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-indigo-700">{row.quotation_no}</div>
                    <div className="text-xs text-slate-500">{row.quotation_status}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{row.customer_name || "-"}</div>
                    <div className="text-xs text-slate-500">{row.customer_email || "No customer email"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{row.sales_order_code || "Order not created yet"}</div>
                    <div className="text-xs text-slate-500">{formatFlowStatus(row.flow_status)}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold">Rs. {Number(row.quotation_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-emerald-700 font-semibold">Rs. {Number(row.amount_paid || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-rose-700 font-semibold">Rs. {Number(row.due_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    {row.invoice_id ? (
                      <>
                        <div className="font-medium text-slate-900">{row.invoice_no}</div>
                        <div className="text-xs text-slate-500">{row.invoice_status}</div>
                      </>
                    ) : (
                      <span className="text-xs font-semibold text-amber-700">Not created</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><PaymentStateBadge state={row.payment_state} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {row.invoice_id ? (
                        <button onClick={() => onView(row.invoice_id)} className="table-btn"><Eye size={14} /></button>
                      ) : (
                        <button onClick={() => handleCreateFromQuotation(row)} disabled={creatingQuotationId === row.quotation_id} className="primary-btn compact-btn">
                          <Plus size={14} /> {creatingQuotationId === row.quotation_id ? "Loading..." : "Create Invoice"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !quotationRows.length && (
                <tr>
                  <td colSpan="9" className="px-4 py-10 text-center text-slate-500">No client-accepted quotations available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Invoices</h3>
            <p className="text-sm text-slate-500">GST and non-GST invoices with draft, approval, PDF, and payment states.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
            <button onClick={onCreate} className="primary-btn"><Plus size={15} /> New Invoice</button>
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-100 px-6 py-4 md:grid-cols-3">
          <select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))} className="input">
            <option value="">All Statuses</option>
            {["DRAFT", "PENDING_APPROVAL", "APPROVED", "SENT", "PARTIALLY_PAID", "PAID", "CANCELLED"].map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select value={filters.invoice_type} onChange={(e) => setFilters((prev) => ({ ...prev, invoice_type: e.target.value }))} className="input">
            <option value="">All Invoice Types</option>
            {["GST", "NON_GST", "PROFORMA", "CREDIT_NOTE"].map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Supply</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Balance Due</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-indigo-700">{row.invoice_no}</div>
                    <div className="text-xs text-slate-500">{row.is_gst_invoice ? "GST" : "NON-GST"}</div>
                  </td>
                  <td className="px-4 py-3">{row.customer_name || "-"}</td>
                  <td className="px-4 py-3">{row.invoice_type}</td>
                  <td className="px-4 py-3">{row.invoice_date}</td>
                  <td className="px-4 py-3">{row.supply_type}</td>
                  <td className="px-4 py-3 font-semibold">Rs. {Number(row.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-semibold text-rose-700">Rs. {Number(row.balance_due || 0).toFixed(2)}</td>
                  <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => onView(row.id)} className="table-btn"><Eye size={14} /></button>
                      <button onClick={() => openPdf(row.id)} className="table-btn"><Download size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr>
                  <td colSpan="9" className="px-4 py-10 text-center text-slate-500">No invoices found.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="9" className="px-4 py-10 text-center text-slate-500">Loading invoices...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .primary-btn { background: rgb(79 70 229); color: white; }
        .secondary-btn, .table-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
        .compact-btn { padding: 0.5rem 0.85rem; }
      `}</style>
    </div>
  );
}

function SummaryCard({ label, value, hint }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{hint}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const palette = {
    DRAFT: "bg-slate-100 text-slate-700",
    PENDING_APPROVAL: "bg-amber-100 text-amber-700",
    APPROVED: "bg-indigo-100 text-indigo-700",
    SENT: "bg-sky-100 text-sky-700",
    PARTIALLY_PAID: "bg-orange-100 text-orange-700",
    PAID: "bg-emerald-100 text-emerald-700",
    CANCELLED: "bg-rose-100 text-rose-700",
  };
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${palette[status] || palette.DRAFT}`}>{status}</span>;
}

function PaymentStateBadge({ state }) {
  const palette = {
    UNPAID: "bg-rose-100 text-rose-700",
    PARTIALLY_PAID: "bg-amber-100 text-amber-700",
    PAID: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${palette[state] || "bg-slate-100 text-slate-700"}`}>{state || "UNPAID"}</span>;
}

function formatFlowStatus(status) {
  const labels = {
    CLIENT_ACCEPTED: "Client accepted quotation",
    SENT_TO_PRODUCTION: "Moved to production",
    IN_PRODUCTION: "In production",
    PRODUCTION_COMPLETED: "Production completed",
    READY_FOR_DISPATCH: "Ready for dispatch",
    DISPATCH_APPROVED: "Dispatch approved",
    DISPATCHED: "Dispatched",
  };
  return labels[status] || status || "-";
}

export default InvoiceList;
